import * as React from "react";

import { BindingPath } from "../../../Engine/Core/CoreTypes";
import {
    AbsoluteModelPath,
    convertToAbsoluteModelPath,
    convertToLegacyPath,
} from "../../../../../Common/ModelPath/ModelPath";
import { useFormObject } from "../../FormObjectProvider";
import { extractNameFromComponent } from "../../TypingUtils";

import { ExecuteFormActionHandler, FormActionHandler } from "./FormActionHandler";
import { useContextualActions } from "./ContextualActionsContext";

export interface FormActionHandlerExecutionInjectableProps {
    executeFormActionHandler: ExecuteFormActionHandler;
}

export interface FormActionHandlerTargetProps {
    bindingPath?: BindingPath | AbsoluteModelPath;
}

export function withFormActionHandlerExecution<P extends FormActionHandlerExecutionInjectableProps>(
    WrappedComponent: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof FormActionHandlerExecutionInjectableProps> & FormActionHandlerTargetProps> {
    function WithFormActionHandlerExecution(
        props: Omit<P, keyof FormActionHandlerExecutionInjectableProps> & FormActionHandlerTargetProps
    ): JSX.Element {
        const form = useFormObject();
        const actions = useContextualActions();

        // @ts-ignore Баг с HOC-ами https://github.com/Microsoft/TypeScript/issues/28938
        const newProps: P = {
            ...props,
            executeFormActionHandler: async (action: FormActionHandler, args?: unknown): Promise<void | unknown> => {
                const bindingPath = props.bindingPath != undefined ? convertToLegacyPath(props.bindingPath) : undefined;
                if (action == undefined) {
                    return;
                }
                if (typeof action === "function") {
                    // eslint-disable-next-line consistent-return
                    return action(
                        form.getHelperFunctionsCallContext(),
                        {
                            bindingPath: bindingPath,
                            field: "value",
                        },
                        args
                    );
                } else {
                    const actionHandler = actions[action.contextualActionName];
                    if (actionHandler == undefined) {
                        throw new Error(
                            `Parent does not contain contextual action handler with name '${action.contextualActionName}'`
                        );
                    }
                    await actionHandler({
                        bindingPath:
                            props.bindingPath != undefined ? convertToAbsoluteModelPath(props.bindingPath) : undefined,
                    });
                }
                // eslint-disable-next-line no-useless-return
                return;
            },
        };

        return <WrappedComponent {...newProps} />;
    }

    WithFormActionHandlerExecution.displayName = `withFormActionHandlerExecution(${extractNameFromComponent(
        WrappedComponent
    )})`;

    return WithFormActionHandlerExecution;
}
